import React, { useEffect, useRef, useState } from 'react';
import { useTranslator, useAppContext } from '@front-activerse/common-components';
import Odometer from 'react-odometerjs';

interface TrackerProgressProps {
  unit: string;
  value: number;
  goal: number;
  isGoalAchieved: boolean;
}

const TrackerProgress: React.FC<TrackerProgressProps> = ({ unit, value, goal, isGoalAchieved }) => {
  const { t } = useTranslator();
  const {
    images: { trackerGoalAchievedCheck },
  } = useAppContext();

  //   Old radius (r=88): circumference = 2π × 88 ≈ 553px
  //   New radius (r=60): circumference = 2π × 60 ≈ 377px
  //   For 270 degrees (3/4 circle): 377 * 0.75 = 283

  const maxDashLength = 283;
  const percentage = Math.min((value / goal) * maxDashLength, maxDashLength);

  // Keep donut visible while animating to 0, then hide after transition ends
  const [isDonutVisible, setIsDonutVisible] = useState(percentage > 0);
  const [isHidingAfterZero, setIsHidingAfterZero] = useState(false);
  const previousPercentageRef = useRef<number>(percentage);

  useEffect(() => {
    const previous = previousPercentageRef.current;

    if (percentage > 0) {
      // Always show when we have something to render
      setIsDonutVisible(true);
      setIsHidingAfterZero(false);
    } else {
      // percentage is 0
      if (previous > 0) {
        // We are animating down to zero; keep visible until transition ends
        setIsDonutVisible(true);
        setIsHidingAfterZero(true);
      } else {
        // Already at zero before; keep it hidden
        setIsDonutVisible(false);
        setIsHidingAfterZero(false);
      }
    }

    previousPercentageRef.current = percentage;
  }, [percentage]);

  const handleTransitionEnd = (event: React.TransitionEvent<SVGCircleElement>) => {
    if (event.propertyName !== 'stroke-dasharray') return;
    // Hide only if we were waiting to hide after reaching zero
    if (isHidingAfterZero && percentage === 0) {
      setIsDonutVisible(false);
      setIsHidingAfterZero(false);
    }
  };

  const isOverLimit = value > goal;
  let leftValue = 0;

  if (isOverLimit) {
    leftValue = value - goal;
  } else {
    leftValue = goal - value;
  }

  return (
    <div className="tracker-chart-container">
      <div className="tracker-chart">
        <div className="chart-background"></div>

        {isDonutVisible && (
          <svg className="svg-donut" width="128px" height="128px">
            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
              <circle
                className={`donut ${isGoalAchieved && 'goal-achieved'}`}
                strokeDasharray={`${percentage}, 900`}
                stroke="#FF6B35" // Orange color for calories
                strokeWidth="8"
                strokeLinecap="round"
                transform="translate(64.000000, 64.000000) scale(1, -1) rotate(-135) translate(-64.000000, -64.000000) "
                cx="64"
                cy="64"
                r="60"
                onTransitionEnd={handleTransitionEnd}
                style={
                  {
                    '--endvalue': percentage,
                  } as React.CSSProperties
                }
              ></circle>
            </g>
          </svg>
        )}

        <div className="text">
          {isOverLimit ? t('tracker.overLimit.title') : t('tracker.leftCalories.title')}
          <span className="current-value">
            {isOverLimit && '+'}
            <Odometer value={leftValue} format="d" />
          </span>
          <span className="text-text-variant-color">{unit}</span>
        </div>
      </div>

      {isGoalAchieved && (
        <div className="goal-achieved-label">
          <img src={trackerGoalAchievedCheck} className="me-1" alt="" />
          {t('tracker.calorieGoalLabel.complete')}
        </div>
      )}
    </div>
  );
};

export default TrackerProgress;
